import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { API_BASE_URL, ApiError } from '@/lib/api'

/**
 * User type based on API response from /api/v1/auth/login.
 *
 * `permissions` carries the resolved effective set (role.permissions ∪
 * user-level overrides) per RBAC Module spec #7 §6.5 + #8 §5.
 */
export interface User {
  userId: string
  email: string
  name: string
  role: string
  /**
   * The RBAC role's id — `role_finance_officer`, `role_bde`. On the wire since the roleId
   * backfill; it was simply never declared here, so every consumer that needed it reached for
   * `role` (the legacy enum) or `roleName` (a label) instead.
   *
   * **Optional, and the absence is meaningful**: a user with no stored role binding sends none,
   * and code comparing it to an approval chain's `approverRole` must read that as *no step is
   * mine* rather than as a match. Never compare NAMES — the gateway forwards the name and the
   * id separately, and they are not interchangeable.
   */
  roleId?: string
  /** The role's display name. A label; never an identifier. */
  roleName?: string
  teams: string[]
  permissions: string[]
  /** Per-screen saved list filters: screenKey -> (filterKey -> value). */
  defaultListFilters?: Record<string, Record<string, string>>
  /**
   * Marked away — on leave or out sick (desk-and-away-contract.md §1): no new tasks, and the open
   * ones are passed on. Drives the user menu's "Mark me as away" / "I'm back". Absent from an
   * older server, and then the menu offers neither.
   */
  away?: boolean
  /** When they were marked away (ISO); null when not away. */
  awaySince?: string | null
  /** userId of whoever marked them away; null when not away. */
  awaySetBy?: string | null
}

/**
 * Auth tokens returned from login
 */
interface AuthTokens {
  accessToken: string
  refreshToken: string
  expiresIn: number
  tokenType: string
}

interface AuthState {
  // State
  user: User | null
  tokens: AuthTokens | null
  expiresAt: number | null
  isAuthenticated: boolean
  isLoading: boolean
  isInitialized: boolean

  // RBAC v1 — Most recent permission refresh timestamp.
  // Used to detect "stale" perms during long sessions / network failures (spec #10 §5.24).
  permissionsLastRefreshed: number | null

  // Runtime-only: true when the access token expired mid-session AND a silent
  // refresh was not possible. Drives the in-place re-auth modal. It does NOT
  // flip isAuthenticated (so the current page/form stays mounted) and is NOT
  // persisted (a fresh page load must never restore a "modal open" state).
  sessionExpired: boolean

  // Actions
  setAuth: (user: User, tokens: AuthTokens) => void
  logout: () => void
  setLoading: (loading: boolean) => void
  setInitialized: (initialized: boolean) => void
  /**
   * Re-fetch /api/v1/auth/me to update permissions + teams in-place.
   * Triggered on window focus (per spec #10 §5.5) and after any 403.
   *
   * `options.silent` (default false) controls whether a successful refresh
   * broadcasts a `permissions_changed` event to other tabs. When this refresh
   * was itself triggered by a broadcast received from another tab, the caller
   * passes `silent: true` to avoid an infinite re-broadcast ping-pong.
   *
   * Returns true if the refresh succeeded, false on failure.
   */
  refreshPermissions: (options?: { silent?: boolean }) => Promise<boolean>

  /**
   * Silent token refresh: exchange the stored (rotating) refresh token for a
   * fresh access token and apply it in-memory. Single-flight — concurrent
   * callers share one in-flight request. Returns the new access token, or null
   * when refresh is not possible (refresh token expired/revoked) — the caller
   * should then invoke `handleAuthExpiry()`.
   */
  refreshSession: () => Promise<string | null>

  /**
   * Called when a request 401s and a silent refresh was not possible. If the
   * app is already running (mid-session), raise the in-place re-auth modal by
   * setting `sessionExpired` WITHOUT clearing auth (so the page/form stays
   * mounted). Otherwise fall back to a hard `logout()` (redirect to /login).
   */
  handleAuthExpiry: () => void
}

const BROADCAST_CHANNEL_NAME = 'freighai-auth'

/**
 * Lazy-initialized BroadcastChannel for cross-tab permission sync.
 * Per RBAC Module spec #10 §5.6.
 */
let _channel: BroadcastChannel | null = null
function getChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null
  if (_channel === null) {
    try {
      _channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME)
    } catch {
      _channel = null
    }
  }
  return _channel
}

const STORAGE_KEY = 'freighai-auth'

/**
 * Shared in-flight refresh promise. Guarantees that N concurrent 401s trigger
 * exactly ONE `/api/v1/auth/refresh` call; all callers await the same promise.
 */
let refreshPromise: Promise<string | null> | null = null

/**
 * Read the freshest refresh token straight from the persisted store (localStorage),
 * NOT from the in-memory copy. Because the backend ROTATES the refresh token on
 * every refresh, a sibling tab may have rotated it out from under this tab; the
 * persisted value is the most up-to-date one this device holds.
 */
function readPersistedRefreshToken(): string | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as { state?: { tokens?: { refreshToken?: string } } }
    return parsed?.state?.tokens?.refreshToken ?? null
  } catch {
    return null
  }
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      // Initial state
      user: null,
      tokens: null,
      expiresAt: null,
      isAuthenticated: false,
      isLoading: false,
      isInitialized: false,
      permissionsLastRefreshed: null,
      sessionExpired: false,

      // Set auth after successful login
      setAuth: (user, tokens) =>
        set({
          user,
          tokens,
          expiresAt: Date.now() + tokens.expiresIn * 1000,
          isAuthenticated: true,
          isLoading: false,
          permissionsLastRefreshed: Date.now(),
          sessionExpired: false,
        }),

      // Clear auth on logout. Dispatches `beforeLogout` window event so the
      // useFormDraft hook can persist in-flight form state to sessionStorage
      // (spec #10 §5.8).
      logout: () => {
        try {
          window.dispatchEvent(new Event('beforeLogout'))
        } catch {
          // ignore — happens during SSR / tests
        }
        set({
          user: null,
          tokens: null,
          expiresAt: null,
          isAuthenticated: false,
          isLoading: false,
          permissionsLastRefreshed: null,
          sessionExpired: false,
        })
        // Broadcast logout to other tabs (spec #10 §5.6)
        const ch = getChannel()
        if (ch) {
          try { ch.postMessage({ type: 'logout' }) } catch { /* ignore */ }
        }
      },

      // Set loading state
      setLoading: (loading) => set({ isLoading: loading }),

      // Set initialized state (after initial auth check)
      setInitialized: (initialized) => set({ isInitialized: initialized }),

      /**
       * Re-fetches `/api/v1/auth/me` and updates the user payload. Called on
       * window focus to pick up admin-driven permission changes (spec #10 §5.5).
       */
      refreshPermissions: async (options?: { silent?: boolean }) => {
        const state = useAuthStore.getState()
        const token = state.tokens?.accessToken
        if (!token || !state.user) return false
        try {
          const url = `${API_BASE_URL}/api/v1/auth/me`
          const response = await fetch(url, {
            headers: {
              Authorization: `Bearer ${token}`,
              ...(API_BASE_URL.includes('ngrok') && { 'ngrok-skip-browser-warning': 'true' }),
              ...(API_BASE_URL.includes('zrok') && { 'skip_zrok_interstitial': 'TRUE' }),
            }
          })
          if (!response.ok) {
            // 401 here means the access token expired — try a silent refresh
            // first (refreshSession also refreshes user+permissions on success).
            // Only surface expiry (modal / logout) if the refresh itself fails.
            if (response.status === 401) {
              const newToken = await useAuthStore.getState().refreshSession()
              if (!newToken) useAuthStore.getState().handleAuthExpiry()
            }
            // Don't throw on 403/etc — the store stays as-is
            return false
          }
          const body = await response.json()
          const refreshed = (body?.data ?? body) as User
          set((s) => ({
            user: s.user ? {
              ...s.user,
              role: refreshed.role,
              teams: refreshed.teams ?? [],
              permissions: refreshed.permissions ?? [],
              // Carried explicitly, like the fields above. `/auth/me` returns
              // this, and the Save/Clear default control calls this refresh to
              // pick up what it just wrote — dropping it here would leave the
              // control showing "Save" forever after a successful save, and
              // (because persist writes on every set) bake the stale value into
              // localStorage so a reload could not recover either.
              defaultListFilters: refreshed.defaultListFilters,
              // Away (desk-and-away-contract.md §1): a manager may have marked this person away
              // (or back) since they signed in. Carried as sent — absent on an older server, which
              // hides the user menu's away item rather than offering a call that is not there.
              away: refreshed.away,
              awaySince: refreshed.awaySince ?? null,
              awaySetBy: refreshed.awaySetBy ?? null,
            } : null,
            permissionsLastRefreshed: Date.now()
          }))
          // Broadcast to other tabs so they refresh too (spec #10 §5.6).
          // Skip when this refresh was itself triggered by an inbound broadcast
          // — prevents ping-pong loops between tabs.
          if (!options?.silent) {
            const ch = getChannel()
            if (ch) {
              try { ch.postMessage({ type: 'permissions_changed' }) } catch { /* ignore */ }
            }
          }
          return true
        } catch (e) {
          if (e instanceof ApiError && e.status === 401) {
            const newToken = await useAuthStore.getState().refreshSession()
            if (!newToken) useAuthStore.getState().handleAuthExpiry()
          }
          return false
        }
      },

      /**
       * Silent, single-flight token refresh. See interface docs.
       */
      refreshSession: () => {
        if (refreshPromise) return refreshPromise
        refreshPromise = (async (): Promise<string | null> => {
          try {
            const current = useAuthStore.getState()
            const currentUserId = current.user?.userId
            // Prefer the freshest persisted refresh token (a sibling tab may
            // have rotated it), falling back to the in-memory copy.
            const rt = readPersistedRefreshToken() ?? current.tokens?.refreshToken ?? null
            if (!rt) return null

            const { refreshAccessToken } = await import('@/services/auth/authService')
            let result = await refreshAccessToken(rt)

            // A null result means the token we used was rejected. If another tab
            // rotated the token underneath us in the meantime, retry once with
            // the newer persisted token before giving up.
            if (!result) {
              const fresh = readPersistedRefreshToken()
              if (fresh && fresh !== rt) {
                result = await refreshAccessToken(fresh)
              }
            }
            if (!result) return null

            // Identity guard: a valid refresh must return the SAME user. If not,
            // the in-memory state belongs to someone else — treat as a failure.
            if (currentUserId && result.user.userId !== currentUserId) {
              return null
            }

            const tokens = {
              accessToken: result.accessToken,
              refreshToken: result.refreshToken,
              expiresIn: result.expiresIn,
              tokenType: result.tokenType,
            }
            const expiresAt = Date.now() + result.expiresIn * 1000
            set({
              user: result.user,
              tokens,
              expiresAt,
              isAuthenticated: true,
              sessionExpired: false,
              permissionsLastRefreshed: Date.now(),
            })

            // Re-hydrate the tenant profile on the new access token (the token
            // used at login may itself now be expired). Fire-and-forget; the
            // tenant store dedupes concurrent hydrations.
            try {
              const { useTenantStore } = await import('@/stores/tenant-store')
              void useTenantStore.getState().hydrate(result.accessToken)
            } catch { /* ignore */ }

            // Propagate the rotated tokens to sibling tabs so their in-memory
            // copy stays valid (and any modal they raised is dismissed).
            const ch = getChannel()
            if (ch) {
              try {
                ch.postMessage({ type: 'tokens_refreshed', user: result.user, tokens, expiresAt })
              } catch { /* ignore */ }
            }
            return result.accessToken
          } catch {
            return null
          } finally {
            refreshPromise = null
          }
        })()
        return refreshPromise
      },

      handleAuthExpiry: () => {
        const state = useAuthStore.getState()
        if (state.isInitialized && state.isAuthenticated) {
          // App is running with in-memory state → raise the in-place modal
          // WITHOUT clearing auth, so the current page/form stays mounted.
          set({ sessionExpired: true })
        } else {
          // Fresh load / already signed out → hard logout + redirect.
          state.logout()
        }
      },
    }),
    {
      name: 'freighai-auth',
      storage: createJSONStorage(() => localStorage),
      // Only persist these fields
      partialize: (state) => ({
        user: state.user,
        tokens: state.tokens,
        expiresAt: state.expiresAt,
        isAuthenticated: state.isAuthenticated,
      }),
      // On rehydration, check token expiry and auto-logout if expired
      onRehydrateStorage: () => (state) => {
        if (state) {
          if (state.isAuthenticated && state.expiresAt && Date.now() >= state.expiresAt) {
            state.logout()
          }
          state.setInitialized(true)

          // Subscribe to cross-tab permission changes (spec #10 §5.6)
          const ch = getChannel()
          if (ch) {
            ch.onmessage = (event) => {
              const msg = event.data as {
                type?: string
                user?: User
                tokens?: AuthTokens
                expiresAt?: number
              }
              if (msg?.type === 'permissions_changed') {
                // Silent refresh — don't re-broadcast (would create a tab-to-tab loop).
                useAuthStore.getState().refreshPermissions({ silent: true })
              } else if (msg?.type === 'tokens_refreshed') {
                // Another tab silently refreshed and ROTATED the tokens. Adopt
                // them so our in-memory refresh token stays valid, and dismiss
                // any re-auth modal we may have raised in the meantime.
                if (msg.tokens && msg.expiresAt) {
                  useAuthStore.setState({
                    user: msg.user ?? useAuthStore.getState().user,
                    tokens: msg.tokens,
                    expiresAt: msg.expiresAt,
                    isAuthenticated: true,
                    sessionExpired: false,
                  })
                }
              } else if (msg?.type === 'logout') {
                // Other tab logged out — clear our state too
                useAuthStore.setState({
                  user: null,
                  tokens: null,
                  expiresAt: null,
                  isAuthenticated: false,
                  permissionsLastRefreshed: null,
                  sessionExpired: false,
                })
              }
            }
          }
        }
      },
    }
  )
)

// Selector for getting the access token (useful for API calls)
export const getAccessToken = () => useAuthStore.getState().tokens?.accessToken
